import { useEffect, useState } from 'react';
import { prototyperService } from '@ideoz/core';
import { indexTurnVersions, type TurnVersion, type TurnVersionsLookup } from './utils/turnVersions';

/**
 * The server's version per turn, for the prototype pills only.
 *
 * The workbench loads the same list, but only once the prototype panel opens (it gates every
 * prototyper request on the panel, so a Clarify-only conversation sends none). The pills are
 * shown with the panel closed, and a build interrupted by a refresh then looked finished until
 * the founder happened to open the panel (seen on local: refresh during "Writing files").
 *
 * `enabled` is whether the conversation has any prototype turn at all; `refreshKey` changes when
 * a turn ends, so a build that finishes or stops in this tab is re-read.
 */
export function usePillTurnVersions(args: {
  projectId: string | null | undefined;
  conversationId: string | null | undefined;
  enabled: boolean;
  refreshKey: string;
}): TurnVersionsLookup | undefined {
  const { projectId, conversationId, enabled, refreshKey } = args;
  const [byTurn, setByTurn] = useState<Map<string, TurnVersion> | null>(null);

  useEffect(() => {
    setByTurn(null);
    if (!projectId || !conversationId || !enabled) return undefined;
    let cancelled = false;
    prototyperService
      .listConversationTurnVersions(projectId, conversationId)
      .then((versions) => {
        if (!cancelled) setByTurn(indexTurnVersions(versions as TurnVersion[]));
      })
      // No list: the pills stay as they were before versions existed.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId, conversationId, enabled, refreshKey]);

  if (!byTurn) return undefined;
  return { status: 'ready', get: (llmInteractionId) => byTurn.get(llmInteractionId) };
}
